import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { AppAside, ShellColumn, useShellFrame } from "@/app-shell";
import { canManageReport } from "@/profitability/access";
import { MarkReviewedButton } from "@/profitability/overview/period-review";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { recordLabel } from "@/profitability/records/objects";
import type { RecordList } from "@/profitability/records/record-panel";
import {
  FindingPage,
  FindingPending,
} from "@/profitability/review/finding-page";
import { useRetryProcessing } from "@/profitability/review/query";
import {
  ReviewColumn,
  reviewQueueList,
} from "@/profitability/review/review-column";
import { ReviewRecordPanel } from "@/profitability/review/review-record-panel";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { useReviewQueue } from "@/profitability/review/use-review-queue";
import { useSelectedFinding } from "@/profitability/review/use-selected-finding";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { ReviewPeriod } from "./review-period";
import { useLinkedFindingFocus } from "./use-linked-finding-focus";
import type { usePageEdge } from "./use-page-edge";
import { usePeriodParams } from "./use-period-params";
import { useReviewLayout } from "./use-review-layout";
import type { TemplatePeriod } from "./use-template-period";

const queueScope = (filter: string, rules: string[]) =>
  JSON.stringify([filter, [...rules].sort()]);

/**
 * Review is its own view. The findings continue the navigation column, the
 * page is the finding, and the record opens in the panel beside it.
 */
export const ReviewStage = ({
  businessId,
  report,
  objects,
  period,
  periodId,
  edge,
}: {
  businessId: string;
  report: ProfitabilityReport;
  objects: RecordObject[];
  period: TemplatePeriod;
  periodId: string | null;
  edge: ReturnType<typeof usePageEdge>;
}) => {
  const { dockColumn, dockRecord, findingsWidth } = useReviewLayout(
    !!edge.chat,
  );
  useShellFrame(dockColumn ? "canvas" : "document");
  const params = usePeriodParams();
  const latestId = period.overview.data?.latest_run_id ?? null;
  const changePeriod = (runId: string | null) => {
    edge.clearEdge();
    params.setPeriod(runId && runId === latestId ? null : runId);
  };
  const spec = useReviewSpec();
  const run = period.run;
  const data = period.overview.data;
  const latest = !data?.latest_run_id || data.latest_run_id === run?.id;
  const retry = useRetryProcessing(businessId, run?.id ?? "");
  const canUpload =
    canManageReport(report) && !!report.published && !report.archived_at;
  const runId =
    periodId ?? (latest || period.periodChanging ? null : (run?.id ?? null));
  const queue = useReviewQueue(businessId, report.id, runId);
  const selected = edge.record;
  const selectedKey = selected
    ? `${selected.ref.dataset}\0${selected.ref.recordId}`
    : null;
  const [details, setDetails] = useState(dockRecord);
  const [pendingScope, setPendingScope] = useState<string | null>(null);
  const scopeKey = queueScope(queue.filter, queue.selectedRules);
  const openedFor = useRef<string | null>(null);
  const queueKey = `${runId ?? "latest"}:${queue.filter}`;
  const list = reviewQueueList(queue.items, spec.rule_order);
  // Once the column is docked, the first finding is selected. The record
  // opens with it only when the row still leaves the finding its width.
  // Closing the panel keeps that finding. A narrow window starts on the queue.
  useEffect(() => {
    if (pendingScope) {
      if (
        pendingScope !== scopeKey ||
        queue.findings.isPlaceholderData ||
        queue.findings.isPending
      )
        return;
      setPendingScope(null);
      openedFor.current = null;
      edge.closeRecord();
      return;
    }
    if (
      !dockColumn ||
      edge.chat ||
      !queue.findings.isSuccess ||
      queue.findings.isPlaceholderData ||
      list.refs.length === 0
    )
      return;
    if (edge.record) {
      openedFor.current = queueKey;
      return;
    }
    if (openedFor.current === queueKey) return;
    openedFor.current = queueKey;
    const ref = list.refs[0];
    if (!ref) return;
    edge.openRecord(ref, list, { keepChat: true });
    setDetails(dockRecord);
  }, [
    dockColumn,
    dockRecord,
    edge,
    list,
    queue.findings.isSuccess,
    queue.findings.isPlaceholderData,
    queue.findings.isPending,
    queueKey,
    pendingScope,
    scopeKey,
  ]);
  const [panelOver, setPanelOver] = useState<{
    key: string;
    ref: RecordRef;
  } | null>(null);
  // Ask Volca takes the same panel, so the record closes when the chat opens.
  if (edge.chat && details) setDetails(false);
  const panelRef =
    (panelOver?.key === selectedKey ? panelOver.ref : null) ??
    selected?.ref ??
    null;
  const { finding, query: selectedFinding } = useSelectedFinding(
    businessId,
    report.id,
    runId,
    selected?.ref ?? null,
    queue.findings.isPlaceholderData ? [] : queue.items,
  );
  useLinkedFindingFocus(finding?.id);
  const object = finding
    ? objects.find((item) => item.dataset.id === finding.dataset)
    : undefined;
  const select = (ref: RecordRef, list: RecordList) =>
    edge.openRecord(ref, list, { keepChat: true });
  const openDetails = () => {
    if (details) {
      setDetails(false);
      return;
    }
    edge.closeChat();
    setDetails(true);
  };
  const ask = (question: string | null) => {
    setDetails(false);
    edge.ask(question, {
      keepRecord: true,
      runId: finding?.run_id ?? undefined,
    });
  };
  const backToQueue = () => {
    openedFor.current = null;
    setDetails(false);
    edge.closeRecord();
  };
  const column = (
    <ReviewColumn
      objects={objects}
      queue={queue}
      selected={selected?.ref ?? null}
      reviewing={period.findingsReview.reviewing}
      heading={
        <ReviewPeriod
          businessId={businessId}
          reportId={report.id}
          reportName={report.name}
          period={period}
          onPeriodChange={changePeriod}
        />
      }
      actions={
        canManageReport(report) && run ? (
          <MarkReviewedButton
            review={period.review}
            label={spec.labels.reviewed}
            asMenuItem
          />
        ) : undefined
      }
      onOpen={select}
      onScopeChange={(filter, rules) =>
        setPendingScope(queueScope(filter, rules))
      }
    />
  );
  // Docked, the queue and the finding are both on screen. Narrow, they take
  // turns: the queue until a finding is chosen, then the finding.
  const showQueue = dockColumn || !selected;
  const showFinding = dockColumn || !!selected;

  return (
    <>
      <section aria-label="Review" className="flex min-h-0 flex-1 flex-col">
        {showQueue &&
          (dockColumn ? (
            <ShellColumn open label="Findings" width={findingsWidth}>
              {column}
            </ShellColumn>
          ) : (
            column
          ))}
        {period.findingsReview.error && (
          <Stack direction="row" gap={3}>
            <Text role="alert" tone="destructive">
              {period.findingsReview.error}
            </Text>
            {canManageReport(report) && (
              <Button
                variant="secondary"
                disabled={retry.isPending}
                onClick={() => retry.mutate("review")}
              >
                Retry checks
              </Button>
            )}
          </Stack>
        )}
        {showFinding && selected && !finding && !dockColumn && (
          <Button variant="ghost" className="self-start" onClick={backToQueue}>
            Findings
          </Button>
        )}
        {showFinding &&
          (finding ? (
            <FindingPage
              canEditRule={canUpload}
              key={finding.id}
              businessId={businessId}
              finding={finding}
              object={object}
              detailsOpen={details}
              onOpenRecord={openDetails}
              onAsk={ask}
              chatOpen={!!edge.chat}
              onBack={dockColumn ? undefined : backToQueue}
            />
          ) : selected && selectedFinding.isError ? (
            <Stack gap={3} className="py-4">
              <Text role="alert" tone="destructive">
                {reportError(selectedFinding.error)}
              </Text>
              <Button
                variant="secondary"
                className="self-start"
                loading={selectedFinding.isFetching}
                onClick={() => void selectedFinding.refetch()}
              >
                Try again
              </Button>
            </Stack>
          ) : queue.findings.isPending ||
            (selected && selectedFinding.isPending) ? (
            <FindingPending />
          ) : (
            <Stack align="center" justify="center" className="min-h-0 flex-1">
              <Text tone="muted">
                {selected
                  ? "This record has no finding in this period."
                  : "Select a finding to review it."}
              </Text>
            </Stack>
          ))}
      </section>
      <AppAside
        open={details && !edge.chat && !!panelRef && !!finding}
        overlay={!dockRecord}
        label={panelRef ? recordLabel(objects, panelRef) : "Record"}
        onClose={() => setDetails(false)}
      >
        {panelRef && finding && (
          <ReviewRecordPanel
            key={JSON.stringify([panelRef.dataset, panelRef.recordId])}
            businessId={businessId}
            reportId={report.id}
            finding={finding}
            selected={panelRef}
            onOpenRecord={(ref, list) => {
              if (list) select(ref, list);
              else if (selectedKey) setPanelOver({ key: selectedKey, ref });
            }}
            onClose={() => setDetails(false)}
            canUpload={canUpload}
            onAsk={ask}
            showFlag={false}
          />
        )}
      </AppAside>
    </>
  );
};
